import { getDocs, query, updateDoc, where } from 'firebase/firestore'
import { db } from '@otw/models/db'

/**
 * Set sold_to_contact_id / selling_to_contact_id on inventory rows that already
 * point at this portal user but lack a CRM contact id (legacy sold data).
 */
export async function rematchSoldVehiclesToContact(
  contactId: string,
  portalUserId: string,
): Promise<number> {
  const trimmedContact = contactId.trim()
  const trimmedUser = portalUserId.trim()
  if (!trimmedContact || !trimmedUser) return 0

  let updated = 0

  const soldSnap = await getDocs(
    query(db.master_inventory, where('sold_to_user.id', '==', trimmedUser)),
  )
  for (const docSnap of soldSnap.docs) {
    const vehicle = docSnap.data()
    if (vehicle.sold_to_contact_id?.trim()) continue
    await updateDoc(docSnap.ref, { sold_to_contact_id: trimmedContact })
    updated += 1
  }

  const sellingSnap = await getDocs(
    query(db.master_inventory, where('selling_to_user.id', '==', trimmedUser)),
  )
  for (const docSnap of sellingSnap.docs) {
    const vehicle = docSnap.data()
    if (vehicle.selling_to_contact_id?.trim()) continue
    await updateDoc(docSnap.ref, { selling_to_contact_id: trimmedContact })
    updated += 1
  }

  return updated
}

export async function remapContactIdsAfterContactMerge(
  secondaryContactId: string,
  primaryContactId: string,
  /** When set, also attach legacy sold rows keyed only by this portal user id. */
  secondaryPortalUserId?: string | null,
): Promise<void> {
  const leadsSnap = await getDocs(
    query(db.leads, where('contactId', '==', secondaryContactId)),
  )
  const inventoryLeadsSnap = await getDocs(
    query(db.inventory_leads, where('contactId', '==', secondaryContactId)),
  )
  const soldSnap = await getDocs(
    query(db.master_inventory, where('sold_to_contact_id', '==', secondaryContactId)),
  )
  const sellingSnap = await getDocs(
    query(db.master_inventory, where('selling_to_contact_id', '==', secondaryContactId)),
  )

  await Promise.all([
    ...leadsSnap.docs.map((docSnap) =>
      updateDoc(docSnap.ref, { contactId: primaryContactId }),
    ),
    ...inventoryLeadsSnap.docs.map((docSnap) =>
      updateDoc(docSnap.ref, { contactId: primaryContactId }),
    ),
    ...soldSnap.docs.map((docSnap) =>
      updateDoc(docSnap.ref, { sold_to_contact_id: primaryContactId }),
    ),
    ...sellingSnap.docs.map((docSnap) =>
      updateDoc(docSnap.ref, { selling_to_contact_id: primaryContactId }),
    ),
  ])

  // Legacy sold rows may only store sold_to_user.id (no contact id). Point those
  // at the surviving contact so they still show after the duplicate is deleted.
  const secondaryUserId = secondaryPortalUserId?.trim()
  if (secondaryUserId) {
    await rematchSoldVehiclesToContact(primaryContactId, secondaryUserId)
  }
}

